import { useCallback, useState, useEffect } from 'react';
import { getSpecies } from '../../../api/registry';

export const useSpeciesData = () => {
  const [typeOptions, setTypeOptions] = useState([]);
  const [typeToUnitsMap, setTypeToUnitsMap] = useState({});
  // species_name → { property_alias → { unit: string } }, e.g.
  // natural_gas → { lhv: { unit: 'Btu/ft³' }, density: { unit: 'g/ft³' }, ... }
  const [typeToPropertiesMap, setTypeToPropertiesMap] = useState({});
  // species_name → conventional energy unit string ('mmBtu', 'MJ', 'kWh').
  // Used by the equation editor to preview `<flow>.energy_lhv` etc. in
  // each species's idiomatic energy unit.
  const [typeToEnergyUnitMap, setTypeToEnergyUnitMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // A failed load and a registry with no species both used to render as an
  // empty dropdown, so a network blip looked like "there are no species".
  // Callers need to tell those apart to show a retryable error.
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const retry = useCallback(() => setReloadToken(t => t + 1), []);

  useEffect(() => {
    const fetchSpeciesData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const { data } = await getSpecies();
        const types = data.species_types.map(species => ({
          value: species.name,
          label: species.label,
        }));

        const unitsMap = {};
        const propertiesMap = {};
        const energyUnitMap = {};

        data.species_types.forEach(species => {
          const units = [];
          const primaryUnitLabel = species.unit.label;

          units.push(species.unit);

          species.convertible_units.forEach(unit => {
            if (unit.label !== primaryUnitLabel) {
              units.push(unit);
            }
          });

          unitsMap[species.name] = units;
          propertiesMap[species.name] = species.properties || {};
          energyUnitMap[species.name] = species.preferred_energy_unit || 'kWh';
        });

        const sortedTypes = types.sort((a, b) => a.label.localeCompare(b.label));

        setTypeOptions(sortedTypes);
        setTypeToUnitsMap(unitsMap);
        setTypeToPropertiesMap(propertiesMap);
        setTypeToEnergyUnitMap(energyUnitMap);
      } catch (err) {
        setTypeOptions([]);
        setTypeToUnitsMap({});
        setTypeToPropertiesMap({});
        setTypeToEnergyUnitMap({});
        setError(err?.response?.data?.error ?? err?.message ?? 'Could not load species types.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpeciesData();
  }, [reloadToken]);

  return {
    typeOptions,
    typeToUnitsMap,
    typeToPropertiesMap,
    typeToEnergyUnitMap,
    isLoading,
    error,
    retry,
  };
};
